/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  ActorFilter,
  Period,
  PeriodResponse,
  WeeklyRow,
  ANTHROPIC_TZ,
  monthOfDay,
  ownersBySlug,
  parseDailyResponse,
  parseMonthlyResponse,
  parseWeeklyResponse,
  shiftDay,
  shiftMonth,
} from './claudeMetricsView';

export type ClaudeMetricsState = {
  data?: PeriodResponse;
  loading: boolean;
  /** Set when the read failed. Never silently rendered as "no data". */
  error?: string;
  /** `org/repo` (lowercased) -> owning team ref. Empty if the catalog lookup failed. */
  owners: ReadonlyMap<string, string>;
  /**
   * The PREVIOUS period's rows (week before / day before), for deltas.
   *
   * Degrades rather than fails: if this second request errors the panel simply shows no
   * deltas. A comparison is secondary information, and losing it must not cost the primary
   * numbers -- which are already in hand by then.
   */
  previousRows?: WeeklyRow[];
  /**
   * True when the catalog lookup itself failed, as opposed to returning no owners.
   * Without this, a persistent catalog failure renders "not in catalog" on every row --
   * which reads as "nobody has claimed these repos", a confident answer to a question
   * nobody asked. It is the same silent-under-report the Reporting-status card exists to
   * prevent, one layer up.
   */
  ownersFailed: boolean;
};

/**
 * Reads one period -- an ISO week (`offset` weeks back; the SERVER resolves and echoes
 * it, the panel never does ISO arithmetic), or one calendar day or month in the
 * viewer's zone (read from raw runs, so only the retention window is reachable).
 *
 * A failure surfaces as `error` rather than an empty period on purpose: this panel's
 * whole job is telling "nothing was spent" apart from "nothing was recorded", and a
 * swallowed 403 or 500 that renders as an empty table defeats that.
 *
 * `reload` re-runs the whole read when incremented -- the refresh button's contract.
 * Everything refetches, including owners: a stale answer that survives refresh would
 * make the button a lie.
 */
export const useClaudeMetrics = (
  period: Period,
  actor?: ActorFilter,
  reload = 0,
  // Passed in rather than read here: the reading zone is a panel-level choice, and
  // a hook that resolved it itself would not refetch when the reader changes it.
  tz: string = ANTHROPIC_TZ,
): ClaudeMetricsState => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);
  const errorApi = useApi(errorApiRef);
  const [state, setState] = useState<ClaudeMetricsState>({
    loading: true,
    owners: new Map(),
    ownersFailed: false,
  });

  // One stable dependency for "which period", so the deps array below is statically
  // checkable. The variants can never collide: each carries its granularity prefix.
  const periodKey =
    period.granularity === 'week'
      ? `week:${period.offset}`
      : period.granularity === 'day'
      ? `day:${period.day}`
      : `month:${period.month}`;

  useEffect(() => {
    let active = true;
    // Stale-while-revalidate: the previous answer stays in hand while the next one is
    // fetched, so a period switch or refresh dims the page instead of collapsing it to a
    // spinner and rebuilding. `loading` is the panel's cue to present it as stale. A
    // FAILED read still clears data (the catch below) -- stale numbers under an error
    // banner would invite reading them as current.
    setState(s => ({ ...s, loading: true, error: undefined }));
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('claude-metrics');
        const url = (p: Period) => {
          const params = new URLSearchParams(
            p.granularity === 'week'
              ? { offset: String(p.offset) }
              : p.granularity === 'day'
              ? { day: p.day }
              : { month: p.month },
          );
          // Raw-backed periods are local-calendar periods: the server computes the
          // boundaries in the viewer's zone. Weeks stay ISO/UTC -- no tz sent.
          if (p.granularity !== 'week') params.set('tz', tz);
          // One key or the other, never both -- the server refuses an ambiguous pair.
          if (actor) params.set(actor.field, actor.login);
          const path =
            p.granularity === 'week'
              ? 'weekly'
              : p.granularity === 'day'
              ? 'daily'
              : 'monthly';
          return `${base}/${path}?${params}`;
        };

        const res = await fetchApi.fetch(url(period));
        if (!res.ok) {
          throw new Error(
            res.status === 403
              ? 'You do not have access to Claude usage metrics.'
              : `Request failed (${res.status})`,
          );
        }
        // Validate the whole envelope, not just the rows. res.json() is `any`, so a cast
        // here would type-check whatever arrived -- and a missing maxOffset or
        // lastReceived fails worse than an error panel: the first silently un-enforces
        // the retention edge, the second throws during render outside this try.
        const json = await res.json();
        const parsed =
          period.granularity === 'week'
            ? parseWeeklyResponse(json)
            : period.granularity === 'day'
            ? parseDailyResponse(json)
            : parseMonthlyResponse(json);
        if (!parsed) {
          throw new Error(
            'Usage data is unreadable. This usually means the frontend and backend ' +
              'versions differ.',
          );
        }
        if (parsed.invalidRows > 0) {
          throw new Error(
            `Usage data is unreadable (${parsed.invalidRows} malformed row(s)). This ` +
              'usually means the frontend and backend versions differ.',
          );
        }
        const data = {
          granularity: period.granularity,
          ...parsed.response,
        } as PeriodResponse;

        // Previous period, for deltas. Fetched after the primary response so a failure
        // here cannot delay or fail the numbers the page is actually about. At the edge
        // of what exists there is no previous period to ask for: the retention edge for
        // weeks, the oldest raw day for days, the first fully-retained month for
        // months (absent oldestDay means no data at all).
        let previous: Period | undefined;
        if (data.granularity === 'week' && period.granularity === 'week') {
          if (period.offset < data.maxOffset) {
            previous = { granularity: 'week', offset: period.offset + 1 };
          }
        } else if (data.granularity === 'day' && period.granularity === 'day') {
          const prevDay = shiftDay(data.day, -1);
          // ISO date strings compare correctly as strings.
          if (data.oldestDay !== undefined && prevDay >= data.oldestDay) {
            previous = { granularity: 'day', day: prevDay };
          }
        } else if (data.granularity === 'month' && period.granularity === 'month') {
          const prevMonth = shiftMonth(data.month, -1);
          // Only when the previous month is FULLY inside raw retention: a baseline
          // truncated by the prune would inflate every delta with no indicator --
          // worse than showing no comparison at all.
          if (
            data.oldestDay !== undefined &&
            monthOfDay(data.oldestDay) < prevMonth
          ) {
            previous = { granularity: 'month', month: prevMonth };
          } else if (
            data.oldestDay !== undefined &&
            data.oldestDay === `${prevMonth}-01`
          ) {
            previous = { granularity: 'month', month: prevMonth };
          }
        }

        let previousRows: WeeklyRow[] | undefined;
        if (previous) {
          const prev = previous;
          previousRows = await fetchApi
            .fetch(url(prev))
            .then(r => {
              // Throw, don't map to undefined: a 403/500 resolves as !ok and would slip
              // past the catch below, silencing exactly the failure it exists to report.
              if (!r.ok) throw new Error(`previous period request failed (${r.status})`);
              return r.json();
            })
            .then(prevJson => {
              const prevParsed =
                prev.granularity === 'week'
                  ? parseWeeklyResponse(prevJson)
                  : prev.granularity === 'day'
                  ? parseDailyResponse(prevJson)
                  : parseMonthlyResponse(prevJson);
              // A truncated baseline is worse than none: dropped rows would inflate every
              // "vs prev" by exactly their share, with no indicator. Exact or absent.
              if (!prevParsed || prevParsed.invalidRows > 0) {
                throw new Error(
                  prevParsed
                    ? `previous period had ${prevParsed.invalidRows} malformed row(s); refusing a fabricated baseline`
                    : 'previous period payload unreadable',
                );
              }
              return prevParsed.response.rows;
            })
            .catch(e => {
              // Reported, not swallowed. The degradation is deliberate -- deltas are
              // secondary and must not cost the primary numbers -- but a persistently
              // failing second fetch would otherwise mean the deltas silently never appear
              // and nobody learns why. This panel's own principle is that absence is data.
              errorApi.post(
                new Error(
                  `Claude usage: period-over-period comparison unavailable (${
                    e instanceof Error ? e.message : e
                  })`,
                ),
              );
              return undefined;
            });
        }

        // Owning teams are a nice-to-have column, so a catalog failure degrades to
        // blank owners rather than failing the whole panel -- the spend numbers are
        // the point, and they are already in hand at this line. But the failure is
        // REPORTED rather than swallowed: a silent fallback made a real bug here
        // (an unaddressable `fields` path) look like "these repos have no owner".
        let ownersFailed = false;
        const owners = await catalogApi
          .getEntities({
            filter: { kind: 'Component' },
            // The whole annotation map, not `metadata.annotations.github.com/project-slug`:
            // `fields` paths are dot-separated, so a key containing dots cannot be
            // addressed and the selector silently matches nothing -- every row would
            // read "not in catalog". Same shape as homeScope.ts and
            // ServiceRepoAutocompleteExtension.tsx.
            fields: ['metadata.annotations', 'spec.owner'],
          })
          .then(r => ownersBySlug(r.items))
          .catch(e => {
            ownersFailed = true;
            errorApi.post(
              new Error(
                `Claude usage: owning teams unavailable (${
                  e instanceof Error ? e.message : e
                })`,
              ),
            );
            return new Map<string, string>();
          });

        if (active) {
          setState({ loading: false, data, owners, ownersFailed, previousRows });
        }
      } catch (e) {
        if (active) {
          setState({
            loading: false,
            owners: new Map(),
            ownersFailed: false,
            error: e instanceof Error ? e.message : String(e),
          });
        }
      }
    })();
    return () => {
      active = false;
    };
    // periodKey stands in for the period object (stable across re-renders with the
    // same value, statically checkable); the callback reads `period` itself, which is
    // in sync by construction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    discoveryApi,
    fetchApi,
    catalogApi,
    errorApi,
    periodKey,
    actor?.field,
    actor?.login,
    reload,
    // Day and month boundaries are computed server-side from this name, so a zone
    // change is a different query, not a relabel -- it must refetch.
    tz,
  ]);

  return state;
};
